/**
 * Bite Balance-specific Fitbit token storage utilities
 * Keeps Fitbit logic isolated within Bite Balance project
 */

const FITBIT_TOKEN_KEY = "glucera_fitbit_access_token";
const FITBIT_REFRESH_TOKEN_KEY = "glucera_fitbit_refresh_token";
const FITBIT_TOKEN_EXPIRY_KEY = "glucera_fitbit_token_expiry";

/**
 * Store Fitbit access token with expiry
 */
export const setFitbitAccessToken = (
  token: string,
  expiresIn: number = 28800
): void => {
  localStorage.setItem(FITBIT_TOKEN_KEY, token);
  // Calculate expiry time (current time + expiresIn seconds)
  const expiryTime = Date.now() + expiresIn * 1000;
  localStorage.setItem(FITBIT_TOKEN_EXPIRY_KEY, expiryTime.toString());
};

/**
 * Store Fitbit refresh token
 */
export const setFitbitRefreshToken = (token: string): void => {
  localStorage.setItem(FITBIT_REFRESH_TOKEN_KEY, token);
};

/**
 * Get Fitbit access token (returns null if expired)
 */
export const getFitbitAccessToken = (): string | null => {
  const token = localStorage.getItem(FITBIT_TOKEN_KEY);
  const expiry = localStorage.getItem(FITBIT_TOKEN_EXPIRY_KEY);

  if (!token) {
    return null;
  }

  // Check if token is expired
  if (expiry && Date.now() > parseInt(expiry)) {
    return null;
  }

  return token;
};

/**
 * Get Fitbit refresh token
 */
export const getFitbitRefreshToken = (): string | null => {
  return localStorage.getItem(FITBIT_REFRESH_TOKEN_KEY);
};

/**
 * Remove all Fitbit tokens
 */
export const clearFitbitAccessToken = (): void => {
  localStorage.removeItem(FITBIT_TOKEN_KEY);
  localStorage.removeItem(FITBIT_REFRESH_TOKEN_KEY);
  localStorage.removeItem(FITBIT_TOKEN_EXPIRY_KEY);
};

/**
 * Check if Fitbit is connected (has valid token OR refresh token)
 */
export const hasFitbitToken = (): boolean => {
  // Check if we have a valid access token
  const accessToken = getFitbitAccessToken();
  if (accessToken) {
    return true;
  }

  // If access token expired, check if we have refresh token
  // The refresh will happen automatically when API is called
  const refreshToken = getFitbitRefreshToken();
  if (refreshToken) {
    return true;
  }

  return false;
};
